"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { MapTrifold, X } from "@phosphor-icons/react";
import dynamic from "next/dynamic";

/**
 * PharmacyMapView est lazy : ne se charge qu'au click utilisateur sur
 * "Voir sur la carte". Économise ~45 KB brotli (leaflet + react-leaflet)
 * sur le bundle initial de toutes les pages pharmacie-de-garde.
 * ssr: false → leaflet utilise window/DOM.
 */
const PharmacyMapView = dynamic(
  () => import("./PharmacyMapView").then((m) => ({ default: m.PharmacyMapView })),
  {
    ssr: false,
    loading: () => (
      <div
        className="h-[420px] w-full animate-pulse rounded-[18px] bg-[var(--sahha-neutral-100,#F1F5F9)]"
        aria-hidden
      />
    ),
  },
);

type Pin = {
  slug: string;
  name: string;
  addressLine1: string;
  phone?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  shiftType: "DAY" | "NIGHT" | "H24";
  shiftLabel: string;
  district?: string | null;
  googleMapsUrl?: string | null;
};

type Props = {
  pins: Pin[];
  centerLat: number;
  centerLng: number;
  cityName: string;
};

/**
 * Toggle map : par défaut affiche un bouton compact "Voir sur la carte (X)".
 * Au click, expand la carte. Permet de garder le scroll léger sur mobile.
 */
export function PharmacyMapToggle(props: Props) {
  const t = useTranslations("pharmacy");
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-[18px] bg-white text-[13.5px] font-extrabold text-[var(--sahha-primary-700)] ring-1 ring-[var(--sahha-border-subtle)] transition active:scale-[0.99] hover:bg-[var(--sahha-primary-50)]"
        aria-expanded={false}
      >
        <MapTrifold weight="duotone" size={18} />
        {t("map.showMap")}
        <span className="rounded-full bg-[var(--sahha-primary-100)] px-2 py-0.5 text-[11px] font-bold text-[var(--sahha-primary-800)]">
          {props.pins.length}
        </span>
      </button>
    );
  }

  return (
    <div className="relative">
      <PharmacyMapView {...props} />
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label={t("map.hideMap")}
        className="absolute end-3 top-3 z-[600] flex h-9 w-9 items-center justify-center rounded-full bg-white text-[var(--sahha-text-700)] shadow-md ring-1 ring-[var(--sahha-border-subtle)]"
      >
        <X weight="bold" size={14} />
      </button>
    </div>
  );
}
